import React, { useEffect, useState, useRef } from 'react';
import { XIcon, CalendarIcon, ClockIcon, UserIcon, MusicIcon, MapPinIcon, FileTextIcon, PlusIcon, SearchIcon, ChevronDownIcon } from 'lucide-react';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { CustomStudentDropdown } from './CustomStudentDropdown';
import { CustomDropdown } from './CustomDropdown';
import { getTeacherStudents } from '../utils/teacherUtils';
import { AddStudentModal } from './modals/AddStudentModal';
import { toast } from 'react-toastify';
import { generateDurationOptions } from '../utils/timeUtils';
import { StudentPicker } from './StudentPicker';
import { TimePickerDropdown } from './TimePickerDropdown';
import { DurationPickerDropdown } from './DurationPickerDropdown';
import { createLesson } from '../src/services/lessonsService';
import type { CreateLessonInput } from '../src/types/lesson';
interface CreateNewLessonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLessonAdded: (lesson: any) => void;
  darkMode?: boolean;
  initialDate?: string;
  initialTime?: string;
  initialStudentId?: string;
  teacherDefaultSettings?: {
    duration: number;
    location: string;
    notes: string;
  } | null;
}
export const CreateNewLessonModal: React.FC<CreateNewLessonModalProps> = ({
  isOpen,
  onClose,
  onLessonAdded,
  darkMode = false,
  initialDate = '',
  initialTime = '',
  initialStudentId = '',
  teacherDefaultSettings = null
}) => {
  const {
    currentUser
  } = useAuth();
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isAddStudentModalOpen, setIsAddStudentModalOpen] = useState(false);
  // Dragging state - OPTIMIZED: Use refs for drag offset to avoid re-renders
  const [isDragging, setIsDragging] = useState(false);
  const dragOffsetRef = useRef({
    x: 0,
    y: 0
  });
  const rafIdRef = useRef<number | null>(null);
  const [dragStartPos, setDragStartPos] = useState({
    x: 0,
    y: 0
  });
  const [modalPosition, setModalPosition] = useState({
    top: 0,
    left: 0
  });
  const [isMobile, setIsMobile] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  // Instrument autosuggest state
  const [isInstrumentDropdownOpen, setIsInstrumentDropdownOpen] = useState(false);
  const [filteredInstruments, setFilteredInstruments] = useState<string[]>([]);
  const [selectedInstrumentIndex, setSelectedInstrumentIndex] = useState(-1);
  const instrumentDropdownRef = useRef<HTMLDivElement>(null);
  const instrumentInputRef = useRef<HTMLInputElement>(null);
  const [justSelectedInstrument, setJustSelectedInstrument] = useState(false);
  // Form state - CRITICAL: Initialize with a flag to track if defaults have been applied
  const [lessonForm, setLessonForm] = useState({
    title: '',
    studentId: initialStudentId,
    date: initialDate || new Date().toISOString().split('T')[0],
    preciseStartTime: initialTime || '09:00',
    duration: 60,
    location: 'Online',
    instrument: '',
    notes: '',
    status: 'confirmed'
  });
  // Track if we've applied defaults to prevent re-applying
  const [defaultsApplied, setDefaultsApplied] = useState(false);
  // CRITICAL FIX: Reset ALL form fields when modal opens
  useEffect(() => {
    if (isOpen) {
      console.log('[CreateNewLessonModal] 🔄 Resetting form on modal open');
      // Reset ALL fields to clean state
      setLessonForm({
        title: '',
        studentId: '',
        date: initialDate || new Date().toISOString().split('T')[0],
        preciseStartTime: initialTime || '09:00',
        duration: 60,
        location: 'Online',
        instrument: '',
        notes: '',
        status: 'confirmed'
      });
      // Reset defaults applied flag so teacher settings can be reapplied
      setDefaultsApplied(false);
      // Clear any validation errors
      setTimeValidationError('');
      setError('');
    }
  }, [isOpen, initialDate, initialTime]);
  // NEW: Update date when initialDate prop changes
  useEffect(() => {
    if (isOpen && initialDate) {
      console.log('[CreateNewLessonModal] Updating date from initialDate:', initialDate);
      setLessonForm(prev => ({
        ...prev,
        date: initialDate
      }));
    }
  }, [isOpen, initialDate]);
  // NEW: Add time validation error state
  const [timeValidationError, setTimeValidationError] = useState('');
  // DIAGNOSTIC: Log when modal opens and receives settings
  useEffect(() => {
    if (isOpen) {
      console.log('[CreateNewLessonModal] 🚪 Modal opened');
      console.log('[CreateNewLessonModal] 📦 Received teacherDefaultSettings:', {
        settings: teacherDefaultSettings,
        hasDuration: teacherDefaultSettings?.duration !== undefined,
        durationValue: teacherDefaultSettings?.duration,
        hasLocation: teacherDefaultSettings?.location !== undefined,
        locationValue: teacherDefaultSettings?.location
      });
      console.log('[CreateNewLessonModal] 📋 Current lessonForm.duration:', lessonForm.duration);
    }
  }, [isOpen, teacherDefaultSettings, lessonForm.duration]);
  // Apply default settings when modal opens - CRITICAL FIX
  useEffect(() => {
    if (isOpen && teacherDefaultSettings && !defaultsApplied) {
      console.log('[CreateNewLessonModal] ✅ Applying teacher default settings:', {
        duration: teacherDefaultSettings.duration,
        location: teacherDefaultSettings.location,
        notes: teacherDefaultSettings.notes
      });
      setLessonForm(prev => {
        const updated = {
          ...prev,
          duration: teacherDefaultSettings.duration,
          location: teacherDefaultSettings.location,
          notes: teacherDefaultSettings.notes
        };
        console.log('[CreateNewLessonModal] 📝 Updated lessonForm:', {
          oldDuration: prev.duration,
          newDuration: updated.duration,
          oldLocation: prev.location,
          newLocation: updated.location
        });
        return updated;
      });
      setDefaultsApplied(true);
    } else if (isOpen && !teacherDefaultSettings) {
      console.log('[CreateNewLessonModal] ⚠️ No teacher default settings provided');
      console.log('[CreateNewLessonModal] 📋 Using hardcoded defaults: duration=60, location=Online');
    }
  }, [isOpen, teacherDefaultSettings, defaultsApplied]);
  // Reset defaults applied flag when modal closes
  useEffect(() => {
    if (!isOpen) {
      setDefaultsApplied(false);
    }
  }, [isOpen]);
  // Comprehensive instrument list
  const allInstruments = [
  // Classical instruments
  'Piano', 'Violin', 'Viola', 'Cello', 'Double Bass', 'Flute', 'Clarinet', 'Oboe', 'Bassoon', 'Trumpet', 'Trombone', 'French Horn', 'Tuba', 'Harp', 'Percussion',
  // Modern/pop instruments
  'Guitar', 'Bass Guitar', 'Drums', 'Voice', 'Saxophone', 'Ukulele', 'Synthesizer', 'Keyboard',
  // Production & composition
  'Composition', 'Arranging', 'Orchestration', 'Songwriting', 'Music Theory', 'Ear Training',
  // DAWs & tech
  'Ableton Live', 'Logic Pro', 'FL Studio', 'Cubase', 'Pro Tools', 'Studio One', 'GarageBand', 'Reaper',
  // Music business & career
  'Music Business', 'Publishing', 'Copyright', 'Recording', 'Mixing', 'Mastering', 'Sound Design', 'Film Scoring'];
  // Location options for the dropdown
  const locationOptions = [{
    value: 'Online',
    label: 'Online'
  }, {
    value: 'In-Person',
    label: 'In-Person'
  }, {
    value: "Student's Home",
    label: "Student's Home"
  }, {
    value: "Teacher's Studio",
    label: "Teacher's Studio"
  }];
  // Check if mobile on mount and window resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 767);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  // Center modal on open
  useEffect(() => {
    if (isOpen && !isMobile) {
      const centerModal = () => {
        if (!modalRef.current) return;
        // Get actual modal dimensions
        const modalWidth = modalRef.current.offsetWidth;
        const modalHeight = modalRef.current.offsetHeight;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        // Calculate centered position
        const left = (viewportWidth - modalWidth) / 2;
        const top = (viewportHeight - modalHeight) / 2;
        // Ensure minimum padding from edges (20px)
        const minPadding = 20;
        const finalLeft = Math.max(minPadding, Math.min(left, viewportWidth - modalWidth - minPadding));
        const finalTop = Math.max(minPadding, Math.min(top, viewportHeight - modalHeight - minPadding));
        setModalPosition({
          top: finalTop,
          left: finalLeft
        });
        // Reset drag offset on every open
        dragOffsetRef.current = {
          x: 0,
          y: 0
        };
      };
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(centerModal);
    }
  }, [isOpen, isMobile]);
  // Load students when the modal opens
  useEffect(() => {
    if (isOpen && currentUser) {
      loadStudents();
    }
  }, [isOpen, currentUser]);
  // Close instrument dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (instrumentDropdownRef.current && !instrumentDropdownRef.current.contains(event.target as Node)) {
        setIsInstrumentDropdownOpen(false);
        setSelectedInstrumentIndex(-1);
      }
    };
    if (isInstrumentDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isInstrumentDropdownOpen]);
  // Drag handlers - OPTIMIZED
  const handleDragStart = (e: React.MouseEvent) => {
    if (isMobile) return;
    // Only allow dragging from header
    if (!headerRef.current?.contains(e.target as Node)) return;
    // Don't start drag if clicking on close button
    if ((e.target as HTMLElement).closest('button')) return;
    e.preventDefault();
    setIsDragging(true);
    setDragStartPos({
      x: e.clientX,
      y: e.clientY
    });
  };
  const handleDrag = (e: MouseEvent) => {
    if (!isDragging || !modalRef.current) return;
    const deltaX = e.clientX - dragStartPos.x;
    const deltaY = e.clientY - dragStartPos.y;
    dragOffsetRef.current = {
      x: deltaX,
      y: deltaY
    };
    // Cancel previous RAF if still pending
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
    }
    // Schedule DOM update on next frame
    rafIdRef.current = requestAnimationFrame(() => {
      if (modalRef.current) {
        const finalTop = modalPosition.top + dragOffsetRef.current.y;
        const finalLeft = modalPosition.left + dragOffsetRef.current.x;
        modalRef.current.style.transform = `translate(${finalLeft - modalPosition.left}px, ${finalTop - modalPosition.top}px)`;
      }
    });
  };
  const handleDragEnd = () => {
    if (!isDragging) return;
    // Cancel any pending RAF
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    // Calculate new position with bounds checking
    const modalWidth = modalRef.current?.offsetWidth || 512;
    const modalHeight = modalRef.current?.offsetHeight || 600;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    let newLeft = modalPosition.left + dragOffsetRef.current.x;
    let newTop = modalPosition.top + dragOffsetRef.current.y;
    // Constrain to viewport bounds
    newLeft = Math.max(0, Math.min(newLeft, viewportWidth - modalWidth));
    newTop = Math.max(0, Math.min(newTop, viewportHeight - modalHeight));
    // Update React state with final position
    setModalPosition({
      top: newTop,
      left: newLeft
    });
    // Reset transform
    if (modalRef.current) {
      modalRef.current.style.transform = '';
    }
    setIsDragging(false);
    dragOffsetRef.current = {
      x: 0,
      y: 0
    };
  };
  // Add global mouse event listeners for dragging - OPTIMIZED: Simplified dependencies
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDrag);
      window.addEventListener('mouseup', handleDragEnd);
      return () => {
        window.removeEventListener('mousemove', handleDrag);
        window.removeEventListener('mouseup', handleDragEnd);
        // Cleanup RAF on unmount
        if (rafIdRef.current) {
          cancelAnimationFrame(rafIdRef.current);
          rafIdRef.current = null;
        }
      };
    }
  }, [isDragging, dragStartPos, modalPosition]);
  // Prevent text selection while dragging
  useEffect(() => {
    if (isDragging) {
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'move';
    } else {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    }
    return () => {
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [isDragging]);
  // Handle instrument input change and filter suggestions
  const handleInstrumentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLessonForm(prev => ({
      ...prev,
      instrument: value
    }));
    // Filter instruments based on input
    if (value.trim()) {
      const filtered = allInstruments.filter(instrument => instrument.toLowerCase().includes(value.toLowerCase()));
      setFilteredInstruments(filtered);
      setIsInstrumentDropdownOpen(filtered.length > 0);
      setSelectedInstrumentIndex(-1);
    } else {
      setFilteredInstruments([]);
      setIsInstrumentDropdownOpen(false);
      setSelectedInstrumentIndex(-1);
    }
  };
  // Handle instrument selection from dropdown
  const handleSelectInstrument = (instrument: string) => {
    setLessonForm(prev => ({
      ...prev,
      instrument
    }));
    setIsInstrumentDropdownOpen(false);
    setSelectedInstrumentIndex(-1);
    setJustSelectedInstrument(true);
    // Blur the input to prevent focus from reopening dropdown
    if (instrumentInputRef.current) {
      instrumentInputRef.current.blur();
    }
    // Reset the flag after a short delay
    setTimeout(() => {
      setJustSelectedInstrument(false);
    }, 200);
  };
  // Handle keyboard navigation for instrument dropdown
  const handleInstrumentKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isInstrumentDropdownOpen || filteredInstruments.length === 0) {
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedInstrumentIndex(prev => prev < filteredInstruments.length - 1 ? prev + 1 : prev);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedInstrumentIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedInstrumentIndex >= 0) {
          handleSelectInstrument(filteredInstruments[selectedInstrumentIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsInstrumentDropdownOpen(false);
        setSelectedInstrumentIndex(-1);
        break;
    }
  };
  // Load students from Firestore
  const loadStudents = async () => {
    if (!currentUser) return;
    setLoadingStudents(true);
    try {
      // Fetch real students from Firebase using the teacherUtils function
      const fetchedStudents = await getTeacherStudents(currentUser.uid);
      // Map the students to the format expected by the dropdown
      const formattedStudents = fetchedStudents.map(student => ({
        id: student.studentId || student.id,
        name: student.name,
        instrument: student.instrument || '',
        email: student.email || ''
      }));
      setStudents(formattedStudents);
      console.log('Loaded students:', formattedStudents);
    } catch (err) {
      console.error('Error loading students:', err);
      setError('Failed to load students. Please try again.');
    } finally {
      setLoadingStudents(false);
    }
  };
  // Handle form input changes
  const handleInputChange = e => {
    const {
      name,
      value
    } = e.target;
    setLessonForm(prev => ({
      ...prev,
      [name]: value
    }));
  };
  // Handle precise time change with validation
  const handlePreciseTimeChange = value => {
    setLessonForm(prev => ({
      ...prev,
      preciseStartTime: value
    }));
    // Clear validation error when user changes time
    if (timeValidationError) {
      setTimeValidationError('');
    }
  };
  // Handle student selection change
  const handleStudentChange = (studentId: string) => {
    setLessonForm(prev => ({
      ...prev,
      studentId
    }));
    // Auto-populate instrument if a student is selected
    if (studentId) {
      const selectedStudent = students.find(s => s.id === studentId);
      if (selectedStudent && selectedStudent.instrument) {
        setLessonForm(prev => ({
          ...prev,
          instrument: selectedStudent.instrument
        }));
      }
    }
  };
  // Handle new student added from the modal
  const handleStudentAdded = newStudent => {
    // Add the new student to the students list
    const updatedStudents = [...students, {
      id: newStudent.studentId || newStudent.id,
      name: newStudent.name,
      instrument: newStudent.instrument || '',
      email: newStudent.email || ''
    }];
    setStudents(updatedStudents);
    // Select the new student in the dropdown
    setLessonForm(prev => ({
      ...prev,
      studentId: newStudent.studentId || newStudent.id,
      instrument: newStudent.instrument || prev.instrument
    }));
    // Close the add student modal
    setIsAddStudentModalOpen(false);
  };
  // Handle form submission
  const handleSubmit = async e => {
    e.preventDefault();
    if (!currentUser) {
      setError('You must be logged in to create a lesson');
      return;
    }
    // NEW: Validate time format before processing
    const time24h = lessonForm.preciseStartTime;
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    if (!timeRegex.test(time24h)) {
      setTimeValidationError('Invalid time format. Please select a valid time.');
      return;
    }
    setSubmitting(true);
    setError('');
    setTimeValidationError('');
    try {
      // Find the selected student
      const selectedStudent = students.find(s => s.id === lessonForm.studentId);
      if (!selectedStudent) {
        throw new Error('Please select a student');
      }
      // CRITICAL: Validate and log the date we're working with
      console.log('[CALENDAR] Raw form date:', lessonForm.date);
      if (!lessonForm.date || lessonForm.date === '') {
        throw new Error('Date is required');
      }
      // Parse date - ensure we're using the EXACT date from the form
      const dateParts = lessonForm.date.split('-');
      console.log('[CALENDAR] Date parts after split:', dateParts);
      if (dateParts.length !== 3) {
        throw new Error(`Invalid date format: ${lessonForm.date}. Expected YYYY-MM-DD`);
      }
      const year = parseInt(dateParts[0]);
      const month = parseInt(dateParts[1]);
      const day = parseInt(dateParts[2]);
      console.log('[CALENDAR] Parsed date components:', {
        year,
        month,
        day
      });
      // Validate parsed values
      if (isNaN(year) || isNaN(month) || isNaN(day)) {
        throw new Error(`Invalid date values: year=${year}, month=${month}, day=${day}`);
      }
      if (month < 1 || month > 12) {
        throw new Error(`Invalid month: ${month}. Must be 1-12`);
      }
      if (day < 1 || day > 31) {
        throw new Error(`Invalid day: ${day}. Must be 1-31`);
      }
      // Parse time - already validated above
      const timeParts = time24h.split(':');
      if (timeParts.length !== 2) {
        throw new Error(`Invalid time format: ${time24h}`);
      }
      const hours = parseInt(timeParts[0]);
      const minutes = parseInt(timeParts[1]);
      console.log('[CALENDAR] Parsed time components:', {
        hours,
        minutes
      });
      if (isNaN(hours) || isNaN(minutes)) {
        throw new Error(`Invalid time values: hours=${hours}, minutes=${minutes}`);
      }
      // NEW: Additional validation for time values
      if (hours < 0 || hours > 23) {
        setTimeValidationError('Invalid hour. Must be 0-23.');
        setSubmitting(false);
        return;
      }
      if (minutes < 0 || minutes > 59) {
        setTimeValidationError('Invalid minutes. Must be 0-59.');
        setSubmitting(false);
        return;
      }
      // Create Date object using EXACT values from form
      // IMPORTANT: JavaScript Date months are 0-indexed (0=January, 11=December)
      // So we subtract 1 from the month value
      const startAt = new Date(year, month - 1, day, hours, minutes, 0, 0);
      console.log('[CALENDAR] Created Date object:', {
        input: {
          year,
          month,
          day,
          hours,
          minutes
        },
        dateObject: startAt,
        iso: startAt.toISOString(),
        local: startAt.toLocaleString(),
        getFullYear: startAt.getFullYear(),
        getMonth: startAt.getMonth(),
        getDate: startAt.getDate(),
        getHours: startAt.getHours(),
        getMinutes: startAt.getMinutes()
      });
      // Verify the Date object has the correct values
      if (startAt.getFullYear() !== year) {
        console.error('[CALENDAR] Year mismatch!', {
          expected: year,
          actual: startAt.getFullYear()
        });
      }
      if (startAt.getMonth() !== month - 1) {
        console.error('[CALENDAR] Month mismatch!', {
          expected: month - 1,
          actual: startAt.getMonth()
        });
      }
      if (startAt.getDate() !== day) {
        console.error('[CALENDAR] Day mismatch!', {
          expected: day,
          actual: startAt.getDate()
        });
      }
      // Get timezone
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      // Prepare lesson input for service
      const lessonInput: CreateLessonInput = {
        title: lessonForm.title || `${lessonForm.instrument || 'Music'} Lesson`,
        studentId: lessonForm.studentId,
        studentName: selectedStudent.name,
        teacherId: currentUser.uid,
        startAt: startAt,
        durationMin: parseInt(lessonForm.duration.toString()),
        location: lessonForm.location,
        instrument: lessonForm.instrument,
        notes: lessonForm.notes,
        timezone: timezone
      };
      console.log('[CALENDAR] Final lesson input:', {
        ...lessonInput,
        startAt: startAt.toISOString(),
        startAtLocal: startAt.toLocaleString()
      });
      // Call the service to create the lesson
      const docId = await createLesson(lessonInput);
      console.log('[CALENDAR] Lesson created successfully with ID:', docId);
      // Show success message FIRST (before any callbacks that might fail)
      toast.success('Lesson created successfully!');
      // Dispatch custom event (wrap in try-catch to prevent errors from affecting success flow)
      try {
        window.dispatchEvent(new CustomEvent('lessonCreated', {
          detail: {
            id: docId,
            ...lessonInput
          }
        }));
      } catch (eventError) {
        console.warn('[CALENDAR] Error dispatching event:', eventError);
      }
      // Call the onLessonAdded callback (wrap in try-catch to prevent errors from affecting success flow)
      if (onLessonAdded) {
        try {
          onLessonAdded({
            id: docId,
            ...lessonInput
          });
        } catch (callbackError) {
          console.warn('[CALENDAR] Error in onLessonAdded callback:', callbackError);
        }
      }
      // Close the modal
      onClose();
    } catch (err) {
      console.error('[CALENDAR] Error creating lesson:', err);
      // Only show error if the actual creation failed
      setError(err.message || 'Failed to create lesson. Please try again.');
      toast.error(err.message || 'Failed to create lesson. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-836">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0" data-id="element-837">
        {/* Background overlay - darkened without blur */}
        <div className="fixed inset-0 transition-opacity" onClick={onClose} data-id="element-838">
          <div className={`absolute inset-0 ${darkMode ? 'bg-black' : 'bg-gray-900'} opacity-75`} data-id="element-839"></div>
        </div>
        {/* Modal panel */}
        <div ref={modalRef} className={`${isMobile ? 'relative inline-block align-bottom' : 'fixed'} rounded-xl text-left transform ${isDragging ? 'transition-none' : 'transition-all'} sm:max-w-lg sm:w-full ${darkMode ? 'bg-gradient-to-br from-navy-800 to-navy-900 border border-navy-700/50 shadow-2xl' : 'bg-white border border-gray-200 shadow-xl'} ${isDragging ? 'cursor-move' : ''}`} style={isMobile ? {} : {
        top: `${modalPosition.top}px`,
        left: `${modalPosition.left}px`,
        userSelect: isDragging ? 'none' : 'auto',
        boxShadow: darkMode ? '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05)' : undefined
      }} data-id="element-840">
          {/* Header - with drag handle */}
          <div ref={headerRef} className={`px-6 py-5 flex items-center justify-between border-b ${darkMode ? 'border-navy-700/50' : 'border-gray-200'} ${!isMobile ? 'cursor-move' : ''}`} onMouseDown={handleDragStart} data-id="element-841">
            <h3 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-842">
              Create New Lesson
            </h3>
            <button onClick={onClose} className={`rounded-lg ${darkMode ? 'hover:bg-navy-700/50' : 'hover:bg-gray-100'} p-1.5 transition-colors`} data-id="element-843">
              <XIcon className="h-5 w-5" data-id="element-844" />
            </button>
          </div>
          {/* Form */}
          <form onSubmit={handleSubmit} data-id="element-845">
            <div className="p-6 space-y-5 overflow-visible" data-id="element-846">
              {/* Error message */}
              {error && <div className="bg-red-500/20 text-red-200 px-4 py-2 rounded-lg text-sm border border-red-500/30" data-id="element-847">
                  {error}
                </div>}
              {/* Title */}
              <div data-id="element-848">
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`} data-id="element-849">
                  Lesson Title
                </label>
                <input type="text" name="title" value={lessonForm.title} onChange={handleInputChange} placeholder="Add lesson title" className={`w-full px-4 py-2.5 rounded-lg ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} border focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20 shadow-sm transition-all`} data-id="element-850" />
              </div>
              {/* Student - REPLACED with StudentPicker */}
              <div className="overflow-visible" data-id="element-851">
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`} data-id="element-852">
                  Student
                </label>
                <StudentPicker value={lessonForm.studentId} onChange={handleStudentChange} onAddStudent={handleStudentAdded} students={students.map(student => ({
                id: student.id,
                name: student.name,
                email: student.email || '',
                instrument: student.instrument || ''
              }))} darkMode={darkMode} loading={loadingStudents} required={true} data-id="element-853" />
              </div>
              {/* NEW: Date Input Field */}
              <div data-id="element-854">
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`} data-id="element-855">
                  Date
                </label>
                <div className="relative" data-id="element-856">
                  <div className="absolute inset-y-0 left-3.5 flex items-center pointer-events-none" data-id="element-857">
                    <CalendarIcon className="h-4 w-4 text-[#3B82F6]" data-id="element-858" />
                  </div>
                  <input type="date" name="date" value={lessonForm.date} onChange={handleInputChange} required className={`w-full max-w-full min-w-0 pl-11 pr-4 py-2.5 rounded-lg ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white' : 'bg-white border-gray-300 text-gray-900'} border focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20 shadow-sm transition-all md:min-w-[auto]`} style={{
                  WebkitAppearance: 'none',
                  MozAppearance: 'textfield'
                }} data-id="element-859" />
                </div>
              </div>
              {/* Duration and Time Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-860">
                {/* Duration */}
                <div data-id="element-861">
                  <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`} data-id="element-862">
                    Duration
                  </label>
                  <DurationPickerDropdown value={lessonForm.duration} onChange={value => setLessonForm(prev => ({
                  ...prev,
                  duration: value
                }))} darkMode={darkMode} required={true} data-id="element-863" />
                </div>
                {/* Time */}
                <div data-id="element-864">
                  <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`} data-id="element-865">
                    Start time
                  </label>
                  <TimePickerDropdown value={lessonForm.preciseStartTime} onChange={handlePreciseTimeChange} darkMode={darkMode} required={true} error={!!timeValidationError} data-id="element-866" />
                  {timeValidationError && <p className="mt-1.5 text-sm text-red-400 flex items-center" data-id="element-867">
                      <span className="mr-1" data-id="element-868">⚠</span>
                      {timeValidationError}
                    </p>}
                </div>
              </div>
              {/* Location and Instrument Row */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-869">
                {/* Location */}
                <div data-id="element-870">
                  <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`} data-id="element-871">
                    Location
                  </label>
                  <CustomDropdown value={lessonForm.location} options={locationOptions} onChange={value => handleInputChange({
                  target: {
                    name: 'location',
                    value
                  }
                })} darkMode={darkMode} icon={<MapPinIcon className="h-5 w-5 text-gray-400" data-id="element-873" />} required={true} data-id="element-872" />
                </div>
                {/* Instrument with Autosuggest */}
                <div className="overflow-visible" data-id="element-874">
                  <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`} data-id="element-875">
                    Instrument
                  </label>
                  <div className="relative overflow-visible" ref={instrumentDropdownRef} data-id="element-876">
                    <div className="relative" data-id="element-877">
                      <div className="absolute inset-y-0 left-3.5 flex items-center pointer-events-none" data-id="element-878">
                        <MusicIcon className="h-4 w-4 text-[#81C784]" data-id="element-879" />
                      </div>
                      <input ref={instrumentInputRef} type="text" name="instrument" value={lessonForm.instrument} onChange={handleInstrumentChange} onKeyDown={handleInstrumentKeyDown} onFocus={() => {
                      // Don't open if we just selected an instrument
                      if (justSelectedInstrument) {
                        return;
                      }
                      if (lessonForm.instrument.trim()) {
                        const filtered = allInstruments.filter(instrument => instrument.toLowerCase().includes(lessonForm.instrument.toLowerCase()));
                        setFilteredInstruments(filtered);
                        setIsInstrumentDropdownOpen(filtered.length > 0);
                      }
                    }} placeholder="Enter instrument" className={`w-full pl-11 pr-4 py-2.5 rounded-lg ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} border focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20 shadow-sm transition-all`} data-id="element-880" />
                    </div>
                    {/* Autosuggest Dropdown */}
                    {isInstrumentDropdownOpen && filteredInstruments.length > 0 && <div className={`absolute z-[9999] w-full mt-1 rounded-lg shadow-lg ${darkMode ? 'bg-navy-800 border border-navy-700/50' : 'bg-white border border-gray-200'} max-h-60 overflow-auto`} style={{
                    boxShadow: darkMode ? '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2)' : '0 10px 25px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.05)'
                  }} data-id="element-881">
                          <ul className="py-1" data-id="element-882">
                            {filteredInstruments.map((instrument, index) => <li key={instrument} className={`px-4 py-2.5 cursor-pointer flex items-center ${index === selectedInstrumentIndex ? darkMode ? 'bg-navy-700/50' : 'bg-gray-100' : darkMode ? 'hover:bg-navy-700/30' : 'hover:bg-gray-50'} ${darkMode ? 'text-white' : 'text-gray-900'} transition-colors`} onClick={() => handleSelectInstrument(instrument)} onMouseEnter={() => setSelectedInstrumentIndex(index)} data-id="element-883">
                                <MusicIcon className="h-4 w-4 text-gray-400 mr-2.5 flex-shrink-0" data-id="element-884" />
                                <span className="text-sm" data-id="element-885">{instrument}</span>
                              </li>)}
                          </ul>
                        </div>}
                  </div>
                </div>
              </div>
              {/* Notes */}
              <div data-id="element-886">
                <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-2`} data-id="element-887">
                  Notes
                </label>
                <div className="relative" data-id="element-888">
                  <div className="absolute top-3.5 left-3.5 flex items-start pointer-events-none" data-id="element-889">
                    <FileTextIcon className="h-4 w-4 text-[#10B981]" data-id="element-890" />
                  </div>
                  <textarea name="notes" value={lessonForm.notes} onChange={handleInputChange} rows={3} placeholder="Any notes about this lesson..." className={`w-full pl-11 pr-4 py-2.5 rounded-lg ${darkMode ? 'bg-navy-900/50 border-navy-700/50 text-white placeholder-gray-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} border focus:border-[#10B981] focus:ring-1 focus:ring-[#10B981]/20 shadow-sm transition-all`} data-id="element-891" />
                </div>
              </div>
            </div>
            {/* Footer with actions */}
            <div className={`px-6 py-4 flex justify-end space-x-3 border-t ${darkMode ? 'border-navy-700/30 bg-navy-900/20' : 'border-gray-200 bg-gray-50'}`} data-id="element-892">
              <button type="button" onClick={onClose} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${darkMode ? 'bg-navy-700/50 text-white hover:bg-navy-700' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`} disabled={submitting} data-id="element-893">
                Cancel
              </button>
              <button type="submit" className={`px-4 py-2 rounded-md border-2 text-sm font-medium transition-colors flex items-center ${darkMode ? 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10' : 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/5'}`} disabled={submitting} data-id="element-894">
                {submitting ? <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-895">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-896"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-897"></path>
                    </svg>
                    Creating...
                  </> : 'Create Lesson'}
              </button>
            </div>
          </form>
        </div>
      </div>
      {/* Add Student Modal - NOW RENDERED */}
      <AddStudentModal isOpen={isAddStudentModalOpen} onClose={() => setIsAddStudentModalOpen(false)} onStudentAdded={handleStudentAdded} darkMode={darkMode} data-id="element-898" />
      {/* Add styles to fix time input visibility and focus issues */}
      <style jsx global data-id="element-899">{`
        /* Hide browser default calendar and time picker icons */
        .custom-date-input::-webkit-calendar-picker-indicator,
        .custom-time-input::-webkit-calendar-picker-indicator {
          opacity: 0;
          width: 100%;
          height: 100%;
          position: absolute;
          top: 0;
          left: 0;
          bottom: 0;
          right: 0;
          cursor: pointer;
        }

        /* Duration picker scrollbar styling to match Start Time picker */
        ul::-webkit-scrollbar {
          width: 6px;
        }
        ul::-webkit-scrollbar-track {
          background: transparent;
        }
        ul::-webkit-scrollbar-thumb {
          background-color: ${darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'};
          border-radius: 3px;
        }
        ul::-webkit-scrollbar-thumb:hover {
          background-color: ${darkMode ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)'};
        }

        /* Mobile-only: Normalize all input field heights to match Date field */
        @media (max-width: 767px) {
          /* Normalize all text-based inputs */
          input[type='text'],
          input[type='email'],
          input[type='tel'],
          input[type='date'] {
            padding-top: 0.625rem !important;
            padding-bottom: 0.625rem !important;
          }
        }
      `}</style>
    </div>;
};